"use client";
import projects, { Proyecto } from "./projects";
import { useCallback, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { Modal } from "@/components/ui/modal";
import { SectionHeading, Kicker } from "@/components/ui/editorial";

const ReactPlayer = dynamic(() => import("react-player"), { ssr: false });

/**
 * Monta el hijo solo cuando el contenedor se acerca al viewport.
 *
 * El reproductor de YouTube descarga ~900 KB de JS de terceros (iframe_api +
 * www-widgetapi + el player) en cuanto se renderiza. Como el vídeo vive muy por
 * debajo del pliegue, cargarlo en el primer render saturaba el hilo principal y
 * disparaba el LCP de la página. El contenedor conserva su `aspect-video`, así
 * que no hay salto de layout ni cambio visual: solo se retrasa la carga.
 */
function useVisibleOnce<T extends HTMLElement>(margen = "300px") {
	const [visible, setVisible] = useState(false);

	// Ref callback en lugar de useEffect: evita `set-state-in-effect` y React 19
	// admite devolver la función de limpieza desde el propio callback.
	const ref = useCallback(
		(nodo: T | null) => {
			if (!nodo || visible) return;
			if (typeof IntersectionObserver === "undefined") {
				setVisible(true);
				return;
			}
			const observer = new IntersectionObserver(
				(entradas) => {
					if (entradas.some((e) => e.isIntersecting)) {
						setVisible(true);
						observer.disconnect();
					}
				},
				{ rootMargin: margen }
			);
			observer.observe(nodo);
			return () => observer.disconnect();
		},
		[margen, visible]
	);

	return { ref, visible };
}

const ProjectsSection = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [proyecto, setProyecto] = useState<Proyecto | null>(null);
	const { ref: videoRef, visible: videoVisible } =
		useVisibleOnce<HTMLDivElement>();

	function openModal(proy: Proyecto) {
		setProyecto(proy);
		setIsOpen(true);
	}

	function closeModal() {
		setIsOpen(false);
		setProyecto(null);
	}

	return (
		<section
			id="proyectos"
			aria-labelledby="proyectos-title"
			className="px-4 xl:px-0 py-16"
		>
			<div className="container max-w-7xl mx-auto space-y-12">
				{/* Header */}
				<header>
					<SectionHeading
						index="04"
						title="Programas y Proyectos"
						kicker="Nuestro Impacto"
					/>
					<p
						id="proyectos-title"
						className="font-body text-lg mt-4 max-w-4xl leading-relaxed text-muted-foreground"
					>
						Iniciativas socioculturales transformadoras que generan un impacto
						real en nuestra comunidad:{" "}
						<span className="font-semibold text-ink">
							alfabetización digital, prevención y sensibilización, formación
							para la empleabilidad y apoyo comunitario integral.
						</span>
					</p>
				</header>

				{/* Projects Grid */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
					{projects.map((proy: Proyecto, index) => (
						<article
							key={proy.id}
							className="group border border-border rounded-xl overflow-hidden"
							aria-labelledby={`proyecto-${proy.id}-titulo`}
						>
							{/*
							 * Sin `aria-label`: el botón envuelve el título y la descripción
							 * del proyecto, así que un nombre accesible corto no contiene el
							 * texto visible y axe lo marca como desajuste
							 * (label-content-name-mismatch). El contenido ya nombra el botón.
							 */}
							<button
								type="button"
								onClick={() => openModal(proy)}
								className="w-full h-full text-left focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-inset"
							>
								<figure>
									{/* Image */}
									<div className="relative overflow-hidden">
										<Image
											src={proy.image}
											alt={proy.name}
											width={500}
											height={280}
											className="w-full aspect-[16/10] object-cover object-center transition-transform duration-700 group-hover:scale-105"
											priority={index < 3}
										/>
										{/* Project number badge */}
										<div className="absolute top-4 left-4 w-9 h-9 bg-ink rounded-full flex items-center justify-center border border-cream/20">
											<span className="text-cream font-display font-semibold text-xs">
												{String(proy.id).padStart(2, "0")}
											</span>
										</div>
									</div>

									{/* Caption */}
									<figcaption className="p-6 border-t border-border">
										<h2
											id={`proyecto-${proy.id}-titulo`}
											className="font-display text-xl font-semibold text-ink mb-3 leading-tight group-hover:text-terracotta transition-colors duration-200"
										>
											{proy.name}
										</h2>

										<div className="space-y-2 font-body text-sm text-muted-foreground">
											<p className="line-clamp-2 leading-relaxed">
												{proy.objetivo}
											</p>
											<span className="inline-flex items-center gap-1 text-terracotta font-medium text-sm">
												Ver detalles
												<svg
													className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M9 5l7 7-7 7"
													/>
												</svg>
											</span>
										</div>
									</figcaption>
								</figure>
							</button>
						</article>
					))}
				</div>

				{/* Modal */}
				<Modal
					contentClassName="border border-border bg-cream rounded-xl"
					open={isOpen}
					onOpenChange={(open) => {
						if (!open) closeModal();
					}}
					title={
						<div className="flex flex-col gap-4">
							{proyecto?.image && (
								<div className="w-full h-44 sm:h-52 relative rounded-lg overflow-hidden border border-border">
									<Image
										src={proyecto.image}
										alt={proyecto.name || "Proyecto"}
										fill
										sizes="(max-width: 640px) 100vw, 600px"
										className="object-cover object-center"
									/>
									{/* Project number in modal */}
									<div className="absolute top-4 left-4 w-9 h-9 bg-ink rounded-full flex items-center justify-center border border-cream/20">
										<span className="text-cream font-display font-semibold text-xs">
											{String(proyecto?.id).padStart(2, "0")}
										</span>
									</div>
								</div>
							)}
							<div>
								<span className="font-display text-2xl sm:text-3xl font-semibold text-ink leading-tight">
									{proyecto?.name}
								</span>
							</div>
						</div>
					}
					size="xl"
					footer={
						<div className="flex justify-end">
							<button
								onClick={closeModal}
								className="font-body px-8 py-3 rounded-full bg-ink text-cream font-medium hover:bg-ink/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ink transition-all duration-200"
								type="button"
							>
								Cerrar
							</button>
						</div>
					}
				>
					<div className="py-2">
						<dl className="divide-y divide-border space-y-3">
							<DetailRow
								label="Población Destinataria"
								value={proyecto?.poblacionDestinataria}
							/>
							<DetailRow
								label="Objetivo"
								value={proyecto?.objetivo}
							/>
							<DetailRow
								label="Creador"
								value={proyecto?.creador}
							/>
							{proyecto?.impartidoPor && (
								<DetailRow
									label="Impartido por"
									value={proyecto.impartidoPor}
								/>
							)}
							{proyecto?.colaboradores && (
								<DetailRow
									label="Colaboradores"
									value={proyecto.colaboradores.join(" · ")}
								/>
							)}
							{proyecto?.protagonistas && (
								<DetailRow
									label="Protagonistas"
									value={proyecto.protagonistas.join(" · ")}
								/>
							)}
						</dl>
					</div>
				</Modal>

				{/* Video Section */}
				<div className="pt-8 border-t border-border">
					<div className="mb-8">
						<Kicker>Descubre Nuestro Impacto</Kicker>
						<h3 className="font-display text-2xl sm:text-3xl font-semibold text-ink mt-2 mb-3">
							Conoce Nuestra Historia
						</h3>
						<p className="font-body text-muted-foreground max-w-2xl leading-relaxed">
							Descubre el impacto real de nuestros proyectos y cómo
							transformamos vidas en nuestra comunidad
						</p>
					</div>

					<div className="flex justify-center">
						<div className="relative w-full max-w-4xl">
							<div className="relative aspect-video rounded-xl overflow-hidden border border-border bg-ink">
								<div ref={videoRef} className="w-full h-full">
									{videoVisible && (
										<ReactPlayer
											controls
											width="100%"
											height="100%"
											src="https://www.youtube.com/watch?v=ENis2QgweXs"
											title="Asociación Equilibrio"
											style={{ borderRadius: "0.75rem" }}
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

// DetailRow component — editorial
function DetailRow({
	label,
	value,
}: {
	label: string;
	value?: string;
}) {
	if (!value) return null;
	return (
		<div className="py-3 sm:grid sm:grid-cols-4 sm:gap-6 first:pt-0">
			<dt className="font-display text-sm font-semibold text-terracotta uppercase tracking-wide">
				{label}
			</dt>
			<dd className="mt-1 font-body text-base text-ink/80 sm:col-span-3 sm:mt-0 leading-relaxed">
				{value}
			</dd>
		</div>
	);
}

export default ProjectsSection;
